import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Fade, makeStyles, Typography } from '@material-ui/core';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import ActionButton from '../actionButton';
import AttributeScore from './attributeScore';
import CompletedInfo from './completedInfo';
import ListWithTooltip from '../listWithTooltip';
import CustomButton from '../customButton';
import ToggleComponent from '../toggleComponent';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { isArrayEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 48,
    width: '100%',
  },
  rootClickable: {
    cursor: 'pointer',
  },
  rootFullWidth: {
    gridTemplateColumns: '1fr',
  },
  content: {
    boxSizing: 'border-box',
    background: ({ color }) =>
      `linear-gradient(90deg, ${color} 0%, ${primary.bluish8} 56.17%)`,
    borderRadius: 4,
    padding: spacing(0, 6),
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    minHeight: 48,
    maxWidth: ({ hasCollapseButton }) =>
      hasCollapseButton ? 'calc(100% - 56px)' : '100%',
  },
  title: {
    marginRight: spacing(2),
  },
  collapseButton: {
    backgroundColor: primary.bluish8,
    borderRadius: 4,
    alignSelf: 'flex-start',
    width: 48,
    height: 48,
    marginLeft: spacing(2),
    '&:hover': {
      backgroundColor: primary.bluish8,
    },
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
    width: 12,
    height: 12,
  },
  expandIconOpened: {
    transform: 'rotate(180deg)',
  },
  commentWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    marginRight: ({ hasAllCommentsButton }) =>
      hasAllCommentsButton ? spacing(4) : spacing(6),
  },
  scoreWrapper: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
  },
  completed: {
    marginLeft: ({ shouldShowScores }) => (shouldShowScores ? spacing(6) : 0),
  },
  completnessCount: {
    textAlign: 'end',
    width: 85,
  },
  colorBox: {
    marginRight: spacing(2),
  },
  usersList: {
    width: 'fit-content',
  },
  userItemsList: {
    alignItems: 'center',
  },
  userMoreItems: {
    backgroundColor: 'transparent',
    fontSize: 16,
    lineHeight: '20px',
    padding: spacing(0, 2),
    marginTop: spacing(0.5),
    maxWidth: 'fit-content',
  },
  label: {
    color: primary.indigo,
    alignItems: 'center',
    display: 'flex',
    marginRight: spacing(2),
  },
  viewAllButton: {
    boxSizing: 'border-box',
    backgroundColor: 'transparent',
    color: primary.indigo,
    padding: spacing(0.5),
    textDecoration: 'underline !important',
    textDecorationStyle: 'solid !important',
    minHeight: 24,
    maxHeight: 24,
    minWidth: 35,
    '&:hover': {
      backgroundColor: primary.bluish9,
    },
  },
  commentsButton: {
    cursor: 'pointer',
    marginRight: spacing(4),
  },
}));

const AttributeInfoBar = ({
  className = null,
  translations,
  attribute,
  averageScore = null,
  reviewersCount = 0,
  hasCollapseButton = false,
  isExpanded = false,
  isClickable = false,
  isScorePercentage = false,
  hasViewAllComments = false,
  hasComments = false,
  shouldShowScores = false,
  shouldDisableIndividualScores = false,
  hasCompletedCount = false,
  onViewIndividualScores = () => {},
  onClickHandler = () => {},
  onViewReviewerComments = () => {},
  onViewAllComments = () => {},
  commentators = [],
}) => {
  const {
    name,
    color,
    isTextReplyAttribute,
    text_reply_questions,
    finishedCount,
  } = attribute;
  const hasAllCommentsButton = commentators.length > 1 || hasViewAllComments;
  const classes = useStyles({
    averageScore,
    color,
    hasCollapseButton,
    shouldShowScores,
    hasAllCommentsButton,
  });

  return (
    <Fade in appear>
      <div
        className={classNames(
          classes.root,
          {
            [classes.rootClickable]: isClickable,
            [classes.rootFullWidth]: !hasCollapseButton,
          },
          className
        )}
        onClick={onClickHandler}
      >
        <div className={classes.content}>
          <TextBoxWithTooltip
            className={classes.title}
            variant="h5"
            text={name}
          />
          {hasComments && (
            <ToggleComponent inKey={isArrayEmpty(commentators) && !isExpanded}>
              {isArrayEmpty(commentators) && !isExpanded ? (
                <ActionButton
                  rootClass={classes.commentsButton}
                  type={ACTION_BUTTON_TYPES.EDIT_COMMENT}
                  disabledTooltipText={translations.reviewHasComments}
                  size="small"
                  isDisabled
                />
              ) : (
                <div className={classes.commentWrapper}>
                  <Typography variant="body1" className={classes.label}>
                    {translations.commentsLabel}
                  </Typography>
                  {!isArrayEmpty(commentators) && (
                    <ListWithTooltip
                      className={classes.usersList}
                      listClass={classes.userItemsList}
                      moreItemsClass={classes.userMoreItems}
                      items={commentators}
                      visibleItemsCount={4}
                      onItemClick={onViewReviewerComments}
                      asyncListProps={{
                        position: 'top',
                      }}
                      isItemClickable={() => true}
                      isUserList
                    />
                  )}
                  {hasAllCommentsButton && (
                    <CustomButton
                      className={classes.viewAllButton}
                      type="textRoundedSmall"
                      onClick={onViewAllComments}
                    >
                      {translations.allLabel}
                    </CustomButton>
                  )}
                </div>
              )}
            </ToggleComponent>
          )}
          {shouldShowScores && (
            <div className={classes.scoreWrapper}>
              <Typography variant="body1" className={classes.label}>
                {translations.scoreLabel}
              </Typography>
              <AttributeScore
                translations={translations}
                isTextReply={
                  Boolean(isTextReplyAttribute || text_reply_questions) &&
                  !isScorePercentage
                }
                averageScore={averageScore}
                onGetScores={onViewIndividualScores}
                shouldDisableIndividualScores={shouldDisableIndividualScores}
                isScorePercentage={isScorePercentage}
              />
            </div>
          )}
          {hasCompletedCount && (
            <CompletedInfo
              className={classes.completed}
              label={translations.completed}
              allReviewers={reviewersCount}
              finishedCount={finishedCount}
            />
          )}
        </div>
        <Fade in={hasCollapseButton} unmountOnExit>
          <div>
            <ActionButton
              className={classes.collapseButton}
              iconClass={classNames(classes.expandIcon, {
                [classes.expandIconOpened]: isExpanded,
              })}
              type={ACTION_BUTTON_TYPES.ARROW}
              size="small"
              onClickHandler={onClickHandler}
            />
          </div>
        </Fade>
      </div>
    </Fade>
  );
};

AttributeInfoBar.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  attribute: PropTypes.object.isRequired,
  commentators: PropTypes.arrayOf(PropTypes.object),
  hasCollapseButton: PropTypes.bool,
  isExpanded: PropTypes.bool,
  hasComments: PropTypes.bool,
  isClickable: PropTypes.bool,
  hasViewAllComments: PropTypes.bool,
  shouldShowScores: PropTypes.bool,
  shouldDisableIndividualScores: PropTypes.bool,
  averageScore: PropTypes.number,
  reviewersCount: PropTypes.number,
  hasCompletedCount: PropTypes.bool,
  onViewIndividualScores: PropTypes.func,
  onClickHandler: PropTypes.func,
  onViewReviewerComments: PropTypes.func,
  onViewAllComments: PropTypes.func,
  isScorePercentage: PropTypes.bool,
};

export default AttributeInfoBar;
